import React, { useState } from 'react';
import { Button } from '../common/Catalyst/button';
import { FieldPicker } from '../common/Custom/FieldPicker';
import { Text } from '../common/Catalyst/text';
import { HoaReturn } from '../clients/rust-api-client';
import { hoaService } from '../clients/ApiClients';
import { apiErrorMessage } from '../utils/apiErrors';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { US_TIMEZONES, timezoneLabel } from './timezones';

interface Props {
  hoa: HoaReturn;
  /** Lets the parent keep its copy of the HOA in step. */
  onChanged?: (hoa: HoaReturn) => void;
}

/**
 * The association's timezone (#599). Owner/Admin, and the association's
 * own Managers — enforced server-side (`can_set_timezone` for the role,
 * `check_hoa_access_with_db` for the assignment). Not rendered for the
 * board. A Manager can open HOAs they are not assigned to, and saving
 * there shows the server's refusal; front-porch does not know
 * assignments, so it does not pretend to.
 *
 * By default the zone comes from the state. That is wrong for an
 * association in the minority half of a state that spans two zones —
 * El Paso is Mountain, Texas is Central — and for most of those the
 * default is *east* of the real zone, which assesses charges early.
 * This is where that gets corrected, ideally at onboarding.
 *
 * The empty option clears the override, so the zone follows the state
 * again.
 */
/**
 * The picker's value for "the state's default". The state keeps `''`, but
 * FieldPicker reads `''` as nothing chosen, so the choice carries this.
 */
const STATE_DEFAULT = '__state_default__';

const TimezonePanel: React.FC<Props> = ({ hoa, onChanged }) => {
  const saved = hoa.timezone_override ?? '';
  const [choice, setChoice] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An override set through the API that is not in the list still has
  // to be shown, or the select would silently display something else.
  const options =
    saved && !US_TIMEZONES.some((z) => z.id === saved)
      ? [...US_TIMEZONES, { id: saved, label: saved }]
      : US_TIMEZONES;

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      const { data } = await hoaService.setHoaTimezone({
        id: hoa.id,
        hoaTimezoneUpdate: { timezone: choice || null },
      });
      setChoice(data.timezone_override ?? '');
      onChanged?.(data);
    } catch (err) {
      setError(
        apiErrorMessage(err, "Couldn't change the timezone. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden mb-4">
      <div className="px-4 py-5 sm:p-6">
        <Text className="font-semibold">Timezone</Text>
        <Text className="mt-1 text-sm">
          Due dates, grace periods, billing periods and amenity hours all
          follow this association’s calendar day. By default the timezone
          comes from the state. Only change it for an association in the
          part of its state that keeps a different time — for example El
          Paso, Texas, or the Florida panhandle.
        </Text>
        <Text className="mt-1 text-sm">
          Existing reservations keep the times they were booked for.
        </Text>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="min-w-[18rem] flex-1">
            <FieldPicker
              label="Timezone"
              value={choice || STATE_DEFAULT}
              onChange={(v) => setChoice(v === STATE_DEFAULT ? '' : v)}
              disabled={saving}
              options={[
                {
                  value: STATE_DEFAULT,
                  label: `The state’s default — ${timezoneLabel(
                    hoa.state_timezone
                  )}`,
                },
                ...options.map((z) => ({
                  value: z.id,
                  label: `${z.label} (${z.id})`,
                })),
              ]}
            />
          </div>
          <Button
            color="sky"
            onClick={save}
            disabled={saving || choice === saved}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>

        <ErrorAlert className="mt-3">{error}</ErrorAlert>
      </div>
    </div>
  );
};

export default TimezonePanel;
